import { useEffect, useRef, useState } from 'react'
import { ConeTecMark, ThumbDownIcon, ThumbUpIcon } from './icons'
import { useDismiss } from '../lib/useDismiss'
import { OneText } from './OneText'
import { linkableRoutes, wikiHrefFor } from '../lib/oneText'
import { linkPageLabel } from '../lib/pages'
import type { HubLang } from '../lib/i18n'
import {
  ONE_REASONS,
  askOne,
  rateOne,
  sourceLineFor,
  type OneAnswer,
  type OneReason,
  type OneSource,
  type OneTurn,
} from '../lib/wiki'
import { laneWords } from '../lib/support'
import { useHubLang } from '../lib/i18n'
import { ASK, ONE_REASONS_ES, type OneRequest } from '../lib/hubCopy'
import { currentHelpPage, helpHref } from '../lib/helpLink'
import { useAskOneSession } from './AskOneSession'

/**
 * Ask One — the panel. HLP-12 (17 Sep) made it a drawer; HLP-44 (31 Oct, One
 * and Help v2 slice S2) made it ONLY an answerer.
 *
 * ── S2: ONE ANSWERS, AND SENDS NOTHING TO ANYBODY ────────────────────────────
 *
 * Until 31 Oct this file also asked people to choose a destination before
 * they had an answer (HLP-16's intents), ran a routing step after a
 * thumbs-down (HLP-1), and offered a one-tap send for an idea or a fault
 * (HLP-41). v2 split the jobs (`one-and-help-v2.html`, D1-D3 decided): Ask One
 * answers; reaching a person is #/help, where the person opens the form.
 * So none of that is here any more, and the reasons it was are recorded on
 * HLP-1, HLP-16 and HLP-41. What replaced it:
 *
 *  - ONE DOOR, ALWAYS VISIBLE: "Need a person? Help" at the foot of the panel,
 *    a plain link to #/help. Nothing to decide, so `stuckFrom` went too (HLP-17
 *    "simpler, not reversed").
 *  - PREFILLED LINKS where a send used to be: after a thumbs-down, and under
 *    an idea or a fault One recognised (ask-one's rule 11 is unchanged). The
 *    link opens Help's form with the person's words in it; THEY press Send.
 *    lib/helpLink.ts builds and re-validates every one.
 *
 * NOTHING LEAVES ONE WITHOUT A FORM THE PERSON OPENED. The suite reads this
 * file and fails if a send (createSupportQuestion, postQuestion,
 * sendSupportFeedback, useRouting) comes back into it.
 *
 * ── IT IS STILL THE ONLY CONVERSATION, AND #/wiki STILL USES ITS PARTS ──────
 *
 * #/wiki's Ask One panel answers on the page with `OneMessage` and `Thumbs`
 * from THIS file. The objection that made the drawer conditional (8 Sep,
 * WIK-19) was against two IMPLEMENTATIONS of the disclosure and the rating;
 * that holds. IF YOU ARE HERE TO ADD A THIRD ASK SURFACE, reuse these.
 *
 * ── DOCKED AT 1280px AND UP, A SHEET BELOW 820px ────────────────────────────
 *
 * From 1280px the panel docks: AppShell marks the shell open and the page
 * narrows beside it (hub.css), so a click on the page is a click on the page,
 * not a dismissal. The width lives in ONE place, the stylesheet: it sets
 * `--onedrawer-docked: 1` inside its 1280px query and `docked()` below reads
 * that, rather than a matchMedia copy of the number that could disagree.
 * Below 820px it is the whole screen and the tab bar stands down (SHL-23).
 *
 * ── IT IS NOT A PORTAL, AND THAT IS LOAD-BEARING ────────────────────────────
 *
 * `createPortal` throws under `renderToString`, so a panel built with one is
 * the single piece of UI the web suite cannot mount — on the surface carrying
 * the AI disclosure and the rate limit. It renders inline at the end of the
 * shell and is positioned by CSS.
 *
 * ── ESCAPE GOES THROUGH useDismiss, NOT A LISTENER ──────────────────────────
 *
 * A React onKeyDown always runs before any document listener, and among
 * document listeners the earlier registration wins — the OUTER surface every
 * time. `useDismiss` keeps a stack and the topmost open surface takes it.
 * (Ctrl+K - ⌘K on a Mac - which OPENS the panel, is AppShell's: it is the
 * launcher's key; lib/askOneKey.ts.)
 *
 * ── THE CONVERSATION IS KEPT FOR THE TAB (1 Nov, HLP-13's session half) ──────
 *
 * It is the provider's (AskOneSession.tsx), mounted once above the routes and
 * mirrored to sessionStorage (lib/askOneSession.ts): following a link in an
 * answer, moving between pages and reloading all keep it; Start over, signing
 * out and View as clear it. A link tapped in an answer navigates; below the
 * docked width the panel closes so the page is visible, and the conversation
 * is there when it reopens. Docked, it stays open beside the new page.
 * HLP-13's server store (90 days) is still not built.
 */

/**
 * THE PAYLOAD BUILDER — every prior turn, oldest first, EXCLUDING the one
 * being asked now.
 *
 * Pure, exported and taking `DrawerMessage[]` rather than closing over state,
 * for one reason worth more than testability: it is the ONE place the client
 * decides what the model gets to read, and the declared intent must not be in
 * it. `DrawerMessage` has no intent member, so an intent cannot reach here at
 * all — the exclusion is a type, not a habit. If a `{ who: 'intent' }` variant
 * is ever added to that union, THIS is the function that has to drop it, and
 * the suite asserts the boundary from both sides.
 *
 * It is not trimmed here. `ask_one_policy.history_turns` is the cap and the
 * server applies it — a slice here would be a second copy of a number somebody
 * will want to tune after launch, and the two would disagree silently
 * (standing rule 11). An assistant turn contributes its ANSWER text only; the
 * envelope is the client contract, not something to read back to the model. A
 * turn One could not answer contributes nothing, because "I don't have an
 * answer to that one yet" is not context, it is noise.
 */
export function historyFrom(messages: DrawerMessage[]): OneTurn[] {
  const out: OneTurn[] = []
  for (const m of messages) {
    if (m.who === 'you') out.push({ role: 'user', text: m.text })
    else if (m.answer.clarify?.length) {
      /* v9. A CLARIFYING EXCHANGE IS NOT CONTEXT — and dropping it is what
         keeps the comms signal honest. Every option is a complete question,
         so the one the person picks needs nothing before it; and with the
         vague question gone too, a clarification on the FIRST turn leaves an
         empty history, so the server logs the picked question as the
         conversation's first (086's `isFollowUp` is derived from exactly
         this). Keep the vague one and the desk records "what about trucks"
         instead of the question the person actually meant. */
      if (out.length && out[out.length - 1].role === 'user') out.pop()
    } else if (m.answer.found && m.answer.answer)
      out.push({ role: 'assistant', text: m.answer.answer })
  }
  return out
}

/**
 * The tap-through sources under an answer (v9).
 *
 * Built from `OneAnswer.sources`, which the SERVER builds from validated
 * citations — so every link here points at something One was really given.
 * Wiki One items link by title to the item; guide entries collapse to one
 * button per PAGE they name ("Open Help"), because five guide entries about
 * stars are one place to go, not five. HLP-37: the page is the entry's own
 * `route` (346), labelled with the page's nav name (lib/pages.ts); an entry
 * with no route - the fences, signing in - gets no button.
 */
/**
 * SUP-2 (1 Nov): THE PLACE TO DO IT, THEN THE GUIDE, THEN WIKI ONE. "How do I
 * report a bug?" used to put the Getting help guide's section first and no
 * ticket form at all. Now, in this order and each page once:
 *   1. pages - where the thing is DONE: a help entry's route and a cited guide
 *      section's `page` ("Open a ticket", "Open Help"), as buttons;
 *   2. guide sections - more to read;
 *   3. Wiki One items.
 * Pure, so the suite asserts the order without a DOM.
 */
export function sourceButtons(
  sources: readonly OneSource[] | undefined,
  lang: HubLang,
): { pages: Array<{ href: string; label: string }>; reading: Array<{ href: string; title: string }> } {
  const pages: Array<{ href: string; label: string }> = []
  const addPage = (route: string | undefined) => {
    if (!route) return
    const label = linkPageLabel(route, lang)
    if (label && !pages.some((p) => p.href === `#${route}`)) pages.push({ href: `#${route}`, label })
  }
  for (const s of sources ?? []) {
    if (s.kind === 'guide') addPage(s.route)
    else if (s.kind === 'guide_section') addPage(s.page)
  }
  const sections = (sources ?? []).flatMap((s) => (s.kind === 'guide_section' ? [{ href: `#${s.route}`, title: s.title }] : []))
  const wiki = (sources ?? []).flatMap((s) => {
    if (s.kind !== 'wiki') return []
    const href = wikiHrefFor(s.id)
    return href && s.title ? [{ href, title: s.title }] : []
  })
  return { pages, reading: [...sections, ...wiki].slice(0, 4) }
}

export function SourceLinks({ one }: { one: OneAnswer }) {
  const lang = useHubLang()
  const c = ASK[lang]
  const { pages, reading } = sourceButtons(one.sources, lang)
  if (reading.length === 0 && pages.length === 0) return null
  return (
    <nav className="onesources" aria-label={c.sourcesLabel}>
      {pages.length > 0 && (
        <div className="onesources__screens">
          {pages.map((p) => (
            <a key={p.href} className="btn btn--sm" href={p.href}>
              {p.label} →
            </a>
          ))}
        </div>
      )}
      {reading.length > 0 && (
        <ul className="onesources__list">
          {reading.map((w) => (
            <li key={w.href}>
              <a className="onesources__link" href={w.href}>{w.title}</a>
            </li>
          ))}
        </ul>
      )}
    </nav>
  )
}

/** A turn on screen. `one` carries its own basis and its own source line —
 *  the label belongs to the MESSAGE, never to the drawer, because a
 *  conversation where only the first reply is labelled is one where the label
 *  has stopped meaning anything. */
export type DrawerMessage =
  | { who: 'you'; text: string }
  | { who: 'one'; answer: OneAnswer }

/* ── the message ─────────────────────────────────────────────────────────── */

/**
 * One turn, rendered. Exported and pure so the suite can assert every state
 * without mounting the drawer or firing an effect — an effect does not run
 * under `renderToString`, so anything that loaded its own data would be
 * unreachable with every assertion passing against an empty string.
 */
export function OneMessage({
  message,
  onPick,
  onEdit,
}: {
  message: DrawerMessage
  /** v9. Ask a clarifying option as a fresh question. Absent → the options
   *  render as plain text (an older turn, or #/wiki's single-shot panel). */
  onPick?: (question: string) => void
  /** v9. Put a question back in the box to be reworded. */
  onEdit?: (question: string) => void
}) {
  const lang = useHubLang()
  const c = ASK[lang]
  if (message.who === 'you') {
    return (
      <div className="onemsg onemsg--you">
        <p className="onemsg__body">{message.text}</p>
      </div>
    )
  }

  const one = message.answer

  /* The check could not run. Say so plainly — a person who asked a question
     and got silence cannot tell "no answer" from "nothing happened". */
  if (one.unavailable) {
    return (
      <div className="onemsg onemsg--one onemsg--off">
        <p className="onemsg__label">{c.unavailableLabel}</p>
        <p className="onemsg__body">{c.unavailableBody}</p>
      </div>
    )
  }

  if (one.rateLimited) {
    return (
      <div className="onemsg onemsg--one onemsg--off">
        <p className="onemsg__label">{c.rateLimitedLabel}</p>
        <p className="onemsg__body">{one.answer}</p>
      </div>
    )
  }

  /* v9. ONE ASKS BEFORE IT GUESSES — but only when the question could mean
     two things the material answers differently. The lead sentence is the
     server's (FALLBACKS.clarifyLead), not the model's, the same posture as
     every other string ask-one returns without a citation. Each option is a
     complete question; picking one asks it fresh. */
  if (one.clarify?.length) {
    return (
      <div className="onemsg onemsg--one onemsg--clarify">
        <p className="onemsg__label">
          <span className="onemsg__mark" aria-hidden="true">
            <ConeTecMark size={14} />
          </span>
          One
        </p>
        <p className="onemsg__body">{one.answer}</p>
        {onPick ? (
          <div className="chips oneclarify">
            {one.clarify.map((o) => (
              <button key={o} type="button" className="chip chip--sm" onClick={() => onPick(o)}>
                {o}
              </button>
            ))}
            {onEdit && (
              <button type="button" className="chip chip--sm chip--quiet" onClick={() => onEdit(one.question)}>
                {c.somethingElse}
              </button>
            )}
          </div>
        ) : (
          <ul className="onetext__list">
            {one.clarify.map((o) => <li key={o}>{o}</li>)}
          </ul>
        )}
      </div>
    )
  }

  const inferred = one.basis === 'inferred'
  const source = sourceLineFor(one, lang)

  return (
    <div className={`onemsg onemsg--one${inferred ? ' onemsg--inferred' : ''}`}>
      <p className="onemsg__label">
        {/* Teal + the mark in a ring IS the machine-authorship disclosure, and
            it is on EVERY reply rather than once at the top of the
            conversation. A disclosure somebody scrolled past is not one. */}
        <span className="onemsg__mark" aria-hidden="true">
          <ConeTecMark size={14} />
        </span>
        One
        {inferred && <span className="pill pill--draft">{c.inferred}</span>}
        {one.unofficialSources && (
          <span className="pill pill--draft">{c.unofficial}</span>
        )}
      </p>
      {/* v10 (S4, Mockup A). The person's own screens One read for this
          answer - derived by the function from the reads that ran, never from
          what the model says. Each is a link to the screen, through the
          router; One reads, the screen is where you act. */}
      {one.found && (one.lookedAt?.length ?? 0) > 0 && (
        <p className="onemsg__looked">
          {c.lookedAt}{' '}
          {(one.lookedAt ?? []).map((l, i) => (
            <span key={l.tool}>
              {i > 0 && ' · '}
              <a className="onemsg__screen" href={`#${l.route}`}>{l.label}</a>
            </span>
          ))}
        </p>
      )}
      {/* v9. How One read a loose question, when it had to. Tappable: the
          cheapest correction there is, and no extra turn when it was right. */}
      {one.found && one.readingAs && (
        <p className="onemsg__reading">
          {c.readingAs(one.readingAs)}
          {onEdit && (
            <>
              {' · '}
              <button type="button" className="linkbtn" onClick={() => onEdit(one.question)}>
                {c.notIt}
              </button>
            </>
          )}
        </p>
      )}
      {/* v9.4. An idea One recognised gets the client's line, not "no answer":
          the server says WHAT it is and the screen says the rest. */}
      {one.found ? (
        <OneText text={one.answer} links={linkableRoutes(one.sources)} />
      ) : (
        <p className="onemsg__body">{one.request ? c.requestBody[one.request] : one.answer}</p>
      )}
      {inferred && (
        <p className="onemsg__note">{c.inferredNote}</p>
      )}
      {/* The source line reads BOTH citation lists. An answer that came purely
          from the platform guide arrives with `used` empty, so a line counting
          only Wiki One ids would render a confident answer with nothing under
          it — which reads as the model inventing, and is the opposite. */}
      {source && <p className="onemsg__source">{source}</p>}
      {one.found && <SourceLinks one={one} />}
    </div>
  )
}

/* ── the door ────────────────────────────────────────────────────────────── */

/**
 * "NEED A PERSON? HELP" — the panel's one door (S2). A link, never a send.
 *
 * With `question`, it is a PREFILLED link: Help opens a ticket with those
 * words in it, Platform support pre-selected (D2), for the person to read,
 * change and send - or not. Without, it is #/help.
 */
export function HelpDoor({ question, from }: { question?: string; from?: string | null }) {
  const c = ASK[useHubLang()]
  const href = question?.trim() ? helpHref({ open: 'ticket', text: question, from, page: currentHelpPage() }) : helpHref()
  return (
    <p className="onehelp">
      {c.helpLead}{' '}
      <a className="onehelp__link" href={href}>
        {c.helpLink}
      </a>
    </p>
  )
}

/**
 * AN IDEA OR A FAULT, AND THE LINK FOR IT (HLP-41, kept as a link in S2).
 *
 * ask-one flags a message that proposes something for the hub, reports
 * something not working, or asks about the work (`request`, rule 11) and
 * stops there — HLP-17: One never names a door. The SCREEN names it, because
 * the screen knows Help exists. Until 31 Oct this was a one-tap send; now it
 * opens the matching form with the words filled in:
 *
 *   idea      → Help ▸ Feedback (the platform desk, D3)
 *   problem   → Help ▸ Ticket, Platform support
 *   technical → Help ▸ Ticket, Technical expert
 *
 * Whether anybody holds that lane today is the FORM's to say (Help's ticket
 * reads `intentRouting` and says when a queue is closed), so this link never
 * promises a person - it promises a form. The cost is one extra tap; the gain
 * is that nothing leaves One without a form the person opened.
 */
export function RequestLink({
  kind,
  question,
  from,
}: {
  kind: OneRequest
  question: string
  from?: string | null
}) {
  const lang = useHubLang()
  const c = ASK[lang]
  const href =
    kind === 'idea'
      ? helpHref({ open: 'feedback', text: question, from, page: currentHelpPage() })
      : helpHref({ open: 'ticket', lane: kind === 'technical' ? 'technical' : 'platform', text: question, from, page: currentHelpPage() })
  const label =
    kind === 'idea'
      ? c.requestLink.idea
      : kind === 'technical'
        ? c.requestLink.technical(laneWords('technical', lang))
        : c.requestLink.problem(laneWords('platform', lang))
  return (
    <div className="onestuck onesuggest">
      <a className="btn btn--sm" href={href}>
        {label} →
      </a>
      <p className="onemsg__note">{c.requestLinkNote}</p>
    </div>
  )
}

/* ── the thumbs ──────────────────────────────────────────────────────────── */

/**
 * THE THUMBS FEED THE DESK, AND A DOWN SHOWS THE DOOR.
 *
 * Up → "anything else?". Down → the five reasons (048's single
 * `rate_one_answer` write, sent once when a reason or Skip is chosen - the
 * table is append-only), then "Thanks — the team will see this", which is
 * true: the rating reaches #/support/ask-one and HLP-40's daily triage.
 *
 * After a down, the door is said again in context, PREFILLED with the
 * question (S2: it used to open the routing step here). A reason and a person
 * are still not alternatives (18 Sep) - the link is there whether or not a
 * reason was given.
 *
 * Exported for #/wiki's panel: a second copy of the rating write and the
 * reason chips is the drift WIK-19 warned about.
 */
export function Thumbs({ one, from }: { one: OneAnswer; from?: string | null }) {
  const lang = useHubLang()
  const c = ASK[lang]
  const [verdict, setVerdict] = useState<'up' | 'down' | null>(null)
  const [sent, setSent] = useState(false)

  // A new answer is a new thing to rate. Without this a "Thanks" from the
  // previous turn would claim the reader had rated this one.
  useEffect(() => {
    setVerdict(null)
    setSent(false)
  }, [one])

  async function up() {
    setVerdict('up')
    setSent(true)
    await rateOne(one, 'up')
  }

  async function down(reason?: OneReason) {
    setSent(true)
    await rateOne(one, 'down', reason)
  }

  if (sent && verdict === 'up') {
    return (
      <div className="onerate onerate--done" aria-live="polite">
        <p className="onerate__q">{c.goodAnythingElse}</p>
      </div>
    )
  }

  if (sent) {
    return (
      <div className="onerate onerate--done" aria-live="polite">
        <p className="onerate__q">{c.thanksTeam}</p>
        <HelpDoor question={one.question} from={from ?? one.logRef} />
      </div>
    )
  }

  if (verdict === 'down') {
    return (
      <div className="onerate" aria-live="polite">
        <p className="onerate__q">{c.whatWasWrong}</p>
        <div className="chips">
          {ONE_REASONS.map((r) => (
            <button key={r.key} type="button" className="chip chip--sm" onClick={() => void down(r.key)}>
              {lang === 'es' ? ONE_REASONS_ES[r.key] : r.label}
            </button>
          ))}
          <button type="button" className="chip chip--sm" onClick={() => void down()}>
            {c.skip}
          </button>
        </div>
        <HelpDoor question={one.question} from={from ?? one.logRef} />
      </div>
    )
  }

  return (
    <div className="onerate">
      <span className="onerate__q">{c.wasUseful}</span>
      <button type="button" className="onethumb" aria-label={c.yesUseful} onClick={() => void up()}>
        <ThumbUpIcon size={16} />
      </button>
      <button type="button" className="onethumb" aria-label={c.noUseful} onClick={() => setVerdict('down')}>
        <ThumbDownIcon size={16} />
      </button>
    </div>
  )
}

/* ── the empty panel ─────────────────────────────────────────────────────── */

/**
 * The page chip and the starters (S2). Both are pure functions of props so
 * the suite can render them; the shell reads the route and the corpus.
 */
export function PageChip({ title, onRemove }: { title: string; onRemove: () => void }) {
  const c = ASK[useHubLang()]
  return (
    <span className="onechip">
      <span className="onechip__dot" aria-hidden="true">◎</span>
      {title}
      <button type="button" className="onechip__x" aria-label={c.pageChipRemove} title={c.pageChipRemove} onClick={onRemove}>
        ✕
      </button>
    </span>
  )
}

export function Starters({ questions, onAsk }: { questions: readonly string[]; onAsk: (q: string) => void }) {
  const c = ASK[useHubLang()]
  if (questions.length === 0) return null
  return (
    <div className="onestarters">
      <p className="onestarters__head">{c.startersHead}</p>
      <ol className="onestarters__list">
        {questions.map((q) => (
          <li key={q}>
            <button type="button" className="onestarters__q" onClick={() => onAsk(q)}>
              {q}
            </button>
          </li>
        ))}
      </ol>
    </div>
  )
}

/* ── the panel ───────────────────────────────────────────────────────────── */

export function AskOneDrawer({
  open,
  onClose,
  locale,
  page = null,
  starters,
}: {
  open: boolean
  onClose: () => void
  /** The reader's locale, passed to both corpora. 084 made help_corpus fall
   *  back per entry, so a Spanish speaker gets the guide rather than silence. */
  locale?: string
  /** The page the person is on - its HELP_COVERAGE key and its name. Null on a
   *  page the map does not name: no chip, rather than a chip saying nothing. */
  page?: { key: string; title: string } | null
  /** Starter questions for a page key (lib/askOnePage.ts `startersFor`).
   *  Taken as a function of props, never loaded here: an effect does not run
   *  under renderToString, and the suite must see the empty state. */
  starters?: (pageKey: string | null) => readonly string[]
}) {
  const c = ASK[useHubLang()]
  /* 1 Nov (HLP-13's session half): the conversation is the provider's
     (AskOneSession.tsx), above the routes and mirrored to this tab's
     sessionStorage, so following a link in an answer or reloading keeps it. */
  const { session, setSession, reset: resetSession, asking, setAsking } = useAskOneSession()
  const { messages, draft } = session
  const setDraft = (d: string) => setSession((s) => ({ ...s, draft: d }))
  /** The chip is removable; another page brings it back. */
  const chipOff = !!page && session.chipOffFor === page.key
  /** 126. The conversation's log row, so a prefilled link to Help can carry
   *  it and the send there stamps where the person went. In the tab only. */
  const logRef = session.logRef

  const panelRef = useRef<HTMLDivElement>(null)
  const askRef = useRef<HTMLTextAreaElement>(null)

  /* Docked (≥1280px) the panel sits BESIDE the page, so a press on the page is
     work on the page and must not close it; Escape and ✕ still do. The answer
     comes from the stylesheet (see the header). */
  const docked = () =>
    !!panelRef.current &&
    typeof getComputedStyle === 'function' &&
    getComputedStyle(panelRef.current).getPropertyValue('--onedrawer-docked').trim() === '1'
  useDismiss(panelRef, open, (viaKeyboard) => {
    if (viaKeyboard || !docked()) onClose()
  })

  useEffect(() => {
    if (!open) return
    askRef.current?.focus()
  }, [open])

  async function send(text?: string) {
    const q = (text ?? draft).trim()
    if (!q || asking) return
    const history = historyFrom(messages)
    setSession((s) => ({ ...s, draft: '', messages: [...s.messages, { who: 'you', text: q }] }))
    setAsking(true)
    try {
      // askOne never throws — One being unavailable comes back as a message
      // rather than an exception, so the conversation survives it.
      // v10 (S4): the chip goes as WORDS - the page's route and name, only
      // while the chip is on. Never an intent, never a lane (HLP-16).
      const answer = await askOne(q, { locale, history, page: chip ? { route: `/${chip.key}`, title: chip.title } : null })
      setSession((s) => ({
        ...s,
        messages: [...s.messages, { who: 'one', answer }],
        logRef: history.length === 0 ? (answer.logRef ?? null) : s.logRef,
      }))
    } finally {
      setAsking(false)
    }
  }

  /** Start over: the conversation goes, here and from the tab. */
  function reset() {
    resetSession()
  }

  /** v9. "Something else" / "Not it?": the question goes back in the box. */
  function editQuestion(q: string) {
    setDraft(q)
    askRef.current?.focus()
  }

  const last = messages[messages.length - 1]
  const lastAnswer = last?.who === 'one' ? last.answer : null
  const lastQuestion = [...messages].reverse().find((m) => m.who === 'you') as
    | { who: 'you'; text: string }
    | undefined
  const requestKind: OneRequest | null =
    lastAnswer?.request && !lastAnswer.unavailable && !lastAnswer.rateLimited ? lastAnswer.request : null
  const chip = page && !chipOff ? page : null
  const firstQuestions = messages.length === 0 && starters ? starters(chip?.key ?? null) : []

  return (
    <div
      className={`onedrawer${open ? ' onedrawer--open' : ''}`}
      // Hidden rather than unmounted: the conversation is React state, and
      // "close it and come back" is the commonest thing anybody will do.
      hidden={!open}
      ref={panelRef}
      role="dialog"
      aria-modal="false"
      aria-label="Ask One"
    >
      <header className="onedrawer__head">
        <p className="onedrawer__title">
          <span className="onemsg__mark" aria-hidden="true">
            <ConeTecMark size={15} />
          </span>
          Ask One
        </p>
        <div className="onedrawer__headactions">
          {messages.length > 0 && (
            <button type="button" className="btn btn--quiet btn--sm" onClick={reset}>
              {c.startOver}
            </button>
          )}
          <button type="button" className="iconbtn" onClick={onClose} aria-label={c.close}>
            ✕
          </button>
        </div>
      </header>

      <div
        className="onedrawer__scroll"
        onClick={(e) => {
          // A link in an answer opens its page; below the docked width the
          // panel steps aside so the page can be seen (it keeps the
          // conversation). Docked, it stays open beside the page.
          const a = (e.target as Element | null)?.closest?.('a[href^="#/"]')
          if (a && !docked()) onClose()
        }}
      >
        {messages.length === 0 && (
          /* IT INTRODUCES ITSELF, INCLUDING THAT IT SENDS NOTHING. */
          <div className="onemsg onemsg--one">
            <p className="onemsg__label">
              <span className="onemsg__mark" aria-hidden="true">
                <ConeTecMark size={14} />
              </span>
              One
            </p>
            <p className="onemsg__body">{c.welcome}</p>
          </div>
        )}

        {/* Only the LAST message is live: a clarifying chip from three turns
            ago re-asked now would answer a conversation that has moved on. */}
        {messages.map((m, i) => {
          const live = i === messages.length - 1 && !asking
          return (
            <OneMessage
              key={i}
              message={m}
              onPick={live ? (q) => void send(q) : undefined}
              onEdit={live ? editQuestion : undefined}
            />
          )
        })}

        {asking && (
          <div className="onemsg onemsg--one onemsg--waiting" aria-live="polite" aria-busy="true">
            <p className="onemsg__label">
              <span className="onemsg__mark onemsg__mark--pulse" aria-hidden="true">
                <ConeTecMark size={14} />
              </span>
              {c.reading}
            </p>
            <div className="onemsg__skeleton" aria-hidden="true">
              <span />
              <span />
              <span />
            </div>
          </div>
        )}

        {/* The thumbs hang off the LAST answer only (048: one row per answer). */}
        {!asking && lastAnswer && !lastAnswer.unavailable && !lastAnswer.rateLimited && lastAnswer.found && (
          <Thumbs one={lastAnswer} from={logRef} />
        )}

        {/* No answer at all: the door, prefilled with the question - it does
            not wait for a thumbs-down, because the person has already seen
            that the answer does not exist. Never under a clarifying question:
            One is asking there, not failing. */}
        {!asking && lastAnswer && lastQuestion && !lastAnswer.found && !requestKind &&
          !lastAnswer.unavailable && !lastAnswer.rateLimited && !lastAnswer.clarify?.length && (
            <div className="onerate">
              <HelpDoor question={lastQuestion.text} from={logRef} />
            </div>
          )}

        {/* An idea, a fault or a question about the work: a link to the form. */}
        {!asking && requestKind && lastQuestion && (
          <RequestLink kind={requestKind} question={lastQuestion.text} from={logRef} />
        )}
      </div>

      {/* The starters sit just above the box, where a thumb is (v2 mockup). */}
      {firstQuestions.length > 0 && <Starters questions={firstQuestions} onAsk={(q) => void send(q)} />}

      <form
        className="onedrawer__ask"
        onSubmit={(e) => {
          e.preventDefault()
          void send()
        }}
      >
        <label className="sr-only" htmlFor="onedrawer-q">
          {c.askLabel}
        </label>
        <div className="onedrawer__box">
          {chip && <PageChip title={chip.title} onRemove={() => setSession((s) => ({ ...s, chipOffFor: chip.key }))} />}
          <textarea
            id="onedrawer-q"
            ref={askRef}
            className="onedrawer__input"
            rows={2}
            maxLength={500}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder={c.placeholder}
            onKeyDown={(e) => {
              // Enter sends, Shift+Enter is a newline. A React onKeyDown, so it
              // runs before useDismiss's document listener; it never claims Escape.
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault()
                void send()
              }
            }}
          />
        </div>
        <button className="btn btn--ai btn--sm" type="submit" disabled={!draft.trim() || asking}>
          {c.ask}
        </button>
      </form>

      <div className="onedrawer__foot">
        <p className="onedrawer__disclose">{c.foot}</p>
        {/* THE ONE DOOR, ALWAYS THERE (v2). A link to #/help, nothing more. */}
        <HelpDoor />
      </div>
    </div>
  )
}
